import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Body from 'react-muscle-highlighter';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, Chip, useTheme } from '@mui/material';
import { muscleLabel, MUSCLE_SLUGS, slugToMuscle, FRONT_MUSCLES, BACK_MUSCLES } from 'components/play/constants';

/**
 * OTRIX Play — anatomical, clickable muscle map (react-muscle-highlighter) styled to the
 * OTRIX monochrome + gold system. Front / back views and a body / list presentation.
 * Clicking a muscle emits its MuscleGroup enum through `onSelect`. Optionally paints a
 * "muscle distribution" (enum -> intensity 1..3) for routine summaries, SimplyFitness style.
 */

// Body-map greys: the muscle base + body outline, mode-aware. Kept local (not global tokens)
// since these are specific to the anatomical figure.
const bodyGreys = (mode) =>
  mode === 'dark'
    ? { base: '#2E353C', outline: 'rgba(255,255,255,0.26)' }
    : { base: '#D7DCE2', outline: 'rgba(15,18,22,0.30)' };

function BodyMap({
  selected = null,
  selectedList = [],
  onSelect = undefined,
  onToggle = undefined,
  distribution = null,
  showModeToggle = true
}) {
  const { t } = useTranslation(['play']);
  const theme = useTheme();
  const [side, setSide] = useState('front');
  const [mode, setMode] = useState('body');

  const greys = bodyGreys(theme.palette.mode);
  const gold = theme.palette.gold.main;
  const goldDark = theme.palette.gold.dark;
  const hairline = theme.palette.hairline.main;
  const intensityColors = ['rgba(249,189,78,0.32)', 'rgba(249,189,78,0.62)', gold];

  // Multi-select mode (onboarding): several muscles selected at once. Falls back to the
  // original single-select behaviour when no `onToggle` is provided.
  const multi = typeof onToggle === 'function';
  const selectedSet = multi ? new Set(selectedList) : null;
  const isActive = (en) => (multi ? selectedSet.has(en) : selected === en);
  const pick = (en) => (multi ? onToggle(en) : onSelect && onSelect(en));

  // Library `data`: a distribution (intensity gradient) takes precedence, else the selected
  // muscle(s) are filled gold. Each of our enums can span several library slugs.
  let highlighted = [];
  if (multi) {
    highlighted = selectedList || [];
  } else if (selected) {
    highlighted = [selected];
  }
  const data = distribution
    ? Object.entries(distribution).flatMap(([en, intensity]) =>
        (MUSCLE_SLUGS[en] || []).map((slug) => ({ slug, intensity })))
    : highlighted.flatMap((en) =>
        (MUSCLE_SLUGS[en] || []).map((slug) => ({
          slug,
          styles: { fill: gold, stroke: goldDark, strokeWidth: 1 }
        })));

  const handlePress = (part) => {
    const en = slugToMuscle[part.slug];
    if (en) pick(en);
  };

  // Selección NEUTRA en chips/filas; el dorado queda solo en el músculo iluminado del SVG.
  const chipSx = (active) => ({ fontWeight: 600, ...(active ? { bgcolor: 'selected.bg', color: 'selected.fg' } : {}) });

  // Sin el toggle no hay forma de salir del modo lista, así que el cuerpo es la única vista.
  const showList = showModeToggle && mode === 'list';
  const listMuscles = side === 'front' ? FRONT_MUSCLES : BACK_MUSCLES;

  return (
    <Stack spacing={2} alignItems="center">
      <Stack direction="row" spacing={1} justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ width: '100%' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="secondary"
          value={side}
          onChange={(_, v) => v && setSide(v)}
          aria-label={t('body.viewAria', { ns: 'play' })}>
          <ToggleButton value="front">{t('body.front', { ns: 'play' })}</ToggleButton>
          <ToggleButton value="back">{t('body.back', { ns: 'play' })}</ToggleButton>
        </ToggleButtonGroup>
        {showModeToggle && (
          <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={mode}
            onChange={(_, v) => v && setMode(v)}
            aria-label={t('body.modeAria', { ns: 'play' })}>
            <ToggleButton value="body">{t('body.viewBody', { ns: 'play' })}</ToggleButton>
            <ToggleButton value="list">{t('body.viewList', { ns: 'play' })}</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {!showList ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            '& svg': { width: '100%', height: 'auto', maxWidth: 240, display: 'block' },
            '& svg path': { transition: 'fill 0.22s cubic-bezier(0.32,0.72,0,1)' },
            '& svg path:hover': { filter: 'brightness(1.12)' }
          }}>
          <Body
            data={data}
            side={side}
            onBodyPartPress={handlePress}
            border={greys.outline}
            defaultFill={greys.base}
            defaultStroke={hairline}
            defaultStrokeWidth={1}
            colors={intensityColors}
          />
        </Box>
      ) : (
        <Stack spacing={1} sx={{ width: '100%' }}>
          {listMuscles.map((en) => {
            const active = isActive(en);
            return (
              <Box
                key={en}
                role="button"
                tabIndex={0}
                aria-pressed={active}
                onClick={() => pick(en)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pick(en);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: active ? 'hairline.strong' : 'hairline.main',
                  bgcolor: active ? 'selected.bg' : 'surface.raised',
                  transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s, background-color 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' },
                  '&:active': { transform: 'scale(0.98)' }
                }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: active ? 'selected.fg' : 'text.secondary', flex: 'none' }} />
                <Typography variant="body2" fontWeight={600}>
                  {muscleLabel(t, en)}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Cardio / full-body — not body regions, offered as chips. Apilados en xs: en fila no
          caben a 320 (este BodyMap lo comparten el home, Mi rutina, Mi Rango y el catálogo). */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Chip
          label={muscleLabel(t, 'CARDIO')}
          onClick={() => pick('CARDIO')}
          variant={isActive('CARDIO') ? 'filled' : 'outlined'}
          sx={chipSx(isActive('CARDIO'))}
        />
        <Chip
          label={muscleLabel(t, 'FULL_BODY')}
          onClick={() => pick('FULL_BODY')}
          variant={isActive('FULL_BODY') ? 'filled' : 'outlined'}
          sx={chipSx(isActive('FULL_BODY'))}
        />
      </Stack>

      {!distribution && !multi && (
        <Typography variant="caption" color="text.secondary">
          {t('body.hint', { ns: 'play' })}
        </Typography>
      )}
    </Stack>
  );
}

BodyMap.propTypes = {
  selected: PropTypes.string,
  selectedList: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  distribution: PropTypes.objectOf(PropTypes.number),
  // El home de Play ya muestra la lista de músculos al lado del cuerpo, así que ahí sobra.
  showModeToggle: PropTypes.bool
};

export default BodyMap;
